import { useId, useState, type RefObject } from 'react';
import styled from 'styled-components';
import type { ChronumSite } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { fleetLeading, fleetType } from '@entities/fleet';
import {
  bindingLabel,
  existingSpelling,
  normalizeCostCenter,
  typo,
  updatedLine,
} from '@entities/chronum';
import { FieldHint, invalidControl } from '@widgets/FleetRegistry/ui';
import {
  CommentField,
  CommentView,
  DetailsGrid,
  DetailsTitle,
  MappingJournal,
  SaveErrorNotice,
  type SaveError,
} from './MappingParts';
import { NARROW } from './ui';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  box-shadow: ${({ theme }) => theme.shadow.sm};

  & .label {
    font-size: ${fleetType.body};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.primary};
    text-wrap: pretty;
  }

  @container chronum (max-width: ${NARROW}) {
    padding: ${({ theme }) => theme.spacing(3)};
  }
`;

const CenterBlock = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Picks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Pick = styled.button`
  min-height: 32px;
  padding: ${({ theme }) => `0 ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.body};
  font-weight: 500;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &[aria-pressed='true'] {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
    font-weight: 600;
  }
`;

const CodeInput = styled(Input)`
  width: 100%;
  max-width: 320px;
  ${invalidControl}

  @container chronum (max-width: ${NARROW}) {
    max-width: none;
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Bound = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const Updated = styled.p`
  margin: 0;
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

/** Что меняется: поле не задано — не трогать. */
export interface SiteChange {
  code?: string | null;
  comment?: string;
}

interface Props {
  site: ChronumSite;
  canWrite: boolean;
  /** Можно ли менять центр затрат: площадку не видит стройка у Руководителя. */
  canSetCenter: boolean;
  /** Подсказки кода: уже используемые и центры январского БДР. */
  suggestions: readonly string[];
  /** Коды, которые уже заведены у площадок: «огм» сохранится их написанием. */
  knownCodes: readonly string[];
  busy: boolean;
  error: SaveError | null;
  rereading: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  commentRef: RefObject<HTMLTextAreaElement | null>;
  onSave: (change: SiteChange) => void;
  onRemove: () => void;
  onCancel: () => void;
  onReread: () => void;
}

/**
 * Привязка площадки: центр затрат (частые коды одной кнопкой или свой),
 * комментарий и журнал правок. Площадке стройки — только комментарий: её часы
 * идут на стройку. Esc и «Закрыть» закрывают без записи.
 */
export function SiteEditor({
  site,
  canWrite,
  canSetCenter,
  suggestions,
  knownCodes,
  busy,
  error,
  rereading,
  inputRef,
  commentRef,
  onSave,
  onRemove,
  onCancel,
  onReread,
}: Props) {
  const inputId = useId();
  const hintId = useId();
  const spellingId = useId();
  const initialCode = site.cost_center_code ?? '';
  const initialComment = site.comment ?? '';
  const [value, setValue] = useState(initialCode);
  const [comment, setComment] = useState(initialComment);
  const code = normalizeCostCenter(value);
  const codeChanged = canSetCenter && code !== normalizeCostCenter(initialCode);
  const commentChanged = comment.trim() !== initialComment.trim();
  const spelling = canSetCenter ? existingSpelling(code, knownCodes) : null;
  const updated = updatedLine(site.updated_at, site.updated_by_name);
  const projects = site.projects.map((p) => `«${p.name}» (${bindingLabel(p.binding)})`).join(', ');

  function submit() {
    if (busy || (!codeChanged && !commentChanged)) return;
    const change: SiteChange = {};
    if (codeChanged) change.code = code || null;
    if (commentChanged) change.comment = comment.trim();
    onSave(change);
  }

  return (
    <DetailsGrid>
      <Form
        aria-label={`Привязка площадки «${site.name}»`}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
      >
        {canSetCenter && canWrite ? (
          <CenterBlock>
            <label className="label" htmlFor={inputId}>
              Центр затрат для площадки «{site.name}»
            </label>
            {suggestions.length > 0 && (
              <Picks role="group" aria-label="Частые центры затрат">
                {suggestions.map((c) => (
                  <Pick
                    key={c}
                    type="button"
                    aria-pressed={code.toLowerCase() === c.toLowerCase()}
                    onClick={() => {
                      setValue(c);
                      inputRef.current?.focus();
                    }}
                  >
                    {c}
                  </Pick>
                ))}
              </Picks>
            )}
            <CodeInput
              id={inputId}
              ref={inputRef}
              value={value}
              maxLength={40}
              autoComplete="off"
              spellCheck={false}
              placeholder="Свой код: например, «Офис»"
              aria-describedby={spelling ? `${hintId} ${spellingId}` : hintId}
              aria-invalid={error && !error.stale ? true : undefined}
              onChange={(e) => setValue(e.target.value)}
            />
            <FieldHint id={hintId}>
              {typo(
                'Короткий код, как в колонках БДР: ОГМ, АУП, «М Строй-К». Регистр не важен: «огм» и «ОГМ» — один центр затрат. Часы площадки пойдут в этот центр затрат.',
              )}
            </FieldHint>
            {spelling && (
              <FieldHint id={spellingId} role="status">
                {typo(`Сохранится как «${spelling}» — такой центр затрат уже заведён.`)}
              </FieldHint>
            )}
          </CenterBlock>
        ) : site.projects.length > 0 ? (
          <Bound>
            {typo(
              `Площадка привязана у Руководителя к ${site.projects.length === 1 ? 'стройке' : 'стройкам'} ${projects}: её часы идут на стройку, центр затрат ей не нужен.`,
            )}
          </Bound>
        ) : (
          <Bound>
            {site.cost_center_code
              ? `Центр затрат: ${site.cost_center_code}.`
              : 'Центр затрат не указан.'}
          </Bound>
        )}

        {canWrite ? (
          <CommentField
            ref={commentRef}
            label="Комментарий"
            value={comment}
            onChange={setComment}
            disabled={busy}
          />
        ) : (
          <div>
            <span className="label">Комментарий</span>
            <CommentView value={site.comment} />
          </div>
        )}

        {canWrite && (
          <Row>
            <Button
              type="submit"
              $variant="primary"
              $size="sm"
              disabled={busy || (!codeChanged && !commentChanged)}
            >
              {busy ? 'Сохраняю…' : 'Сохранить'}
            </Button>
            <Button type="button" $variant="ghost" $size="sm" onClick={onCancel} disabled={busy}>
              {codeChanged || commentChanged ? 'Отмена' : 'Закрыть'}
            </Button>
            {canSetCenter && initialCode && (
              <Button type="button" $variant="ghost" $size="sm" onClick={onRemove} disabled={busy}>
                Снять центр затрат
              </Button>
            )}
          </Row>
        )}
        {!canWrite && (
          <Row>
            <Button type="button" $variant="ghost" $size="sm" onClick={onCancel}>
              Закрыть
            </Button>
          </Row>
        )}
        {error && (
          <SaveErrorNotice error={error} busy={rereading} onReread={onReread} onRetry={submit} />
        )}
        {updated && <Updated>{updated}</Updated>}
      </Form>
      <section aria-label={`Журнал правок площадки «${site.name}»`}>
        <DetailsTitle>Журнал правок</DetailsTitle>
        <MappingJournal entity="site" entityKey={site.id} categories={[]} />
      </section>
    </DetailsGrid>
  );
}
